import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpenCheck, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
export function EditorialPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Editorial Policy & Medical Disclaimer | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-2509" data-mp="2vfqx0vb1be24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2510" data-mp="3o4ekmxj9afiu">
        <header className="mb-12" data-id="element-2511" data-mp="3a4dofvdxrw9p">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-2512" data-mp="3a3byylw6b78g">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2513" data-mp="1ypgj1axg2ug6" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-2514" data-mp="22qtnphoswrli">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-2515" data-mp="22sd55bm718ov">
              <BookOpenCheck className="w-8 h-8" data-id="element-2516" data-mp="3u7kyz3tfbu4j" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-2517" data-mp="3p7oul3gte0og">
              Editorial Policy
            </h1>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2518" data-mp="73dgeg9i2y7z">
            How we research, write, and review the nutrition and ingredient
            content published across IntelliEats.
          </p>
          <p className="text-slate-500 text-sm mt-2" data-id="element-2519" data-mp="gy5i9wa4878k">
            Last updated: July 9, 2026
          </p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-2520" data-mp="bz2j2sgtgv8i">
          <section data-id="element-2521" data-mp="qp9rsgh0uzw0">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2522" data-mp="qrmawwyingan">
              Our Editorial Mission
            </h2>
            <p data-id="element-2523" data-mp="zq819ea8bzlz">
              IntelliEats exists to help everyday shoppers understand what is in
              their packaged food. Every article, guide, and in-app explanation
              is written to be clear, accurate, and genuinely useful — never to
              create fear or promote a specific product. Our goal is awareness,
              not alarm.
            </p>
          </section>

          <section data-id="element-2524" data-mp="1ex37ok8vdz1">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2525" data-mp="1eevnvs4tdbt">
              How We Research Content
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-2526" data-mp="666n08j43n30">
              {['We rely on reputable, authoritative sources such as the FDA, USDA, WHO, NIH, CDC, and peer-reviewed nutritional research.', 'Ingredient information reflects the current scientific consensus and clearly distinguishes established facts from areas where evidence is still emerging.', 'When a topic is debated (for example, certain additives or sweeteners), we present the balance of evidence rather than a one-sided claim.', 'We cite our primary references at the end of each article so you can read the source material yourself.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-2527" data-mp="2jdo5h2bvy6r0" data-mpi={i}>
                  <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-2528" data-mp="2ekv0qegf2yex"></span>
                  <span data-id="element-2529" data-mp="29m0hxcig99ut">{item}</span>
                </li>)}
            </ul>
          </section>

          <section data-id="element-2530" data-mp="25l87wh1r70xh">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2531" data-mp="25lzuj2ddhobm">
              Accuracy & Updates
            </h2>
            <p data-id="element-2532" data-mp="32a66698ppgze">
              Nutrition science evolves. We review our articles periodically and
              update them when guidelines, regulations, or the weight of
              evidence changes. If you spot something that appears inaccurate or
              out of date, please let us know and we will review it promptly.
            </p>
          </section>

          <section data-id="element-2533" data-mp="1hvdwm3x31kfc">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2534" data-mp="1hvvut63b28pw">
              Independence & Transparency
            </h2>
            <p data-id="element-2535" data-mp="1cy41q8qxcgk4">
              Our ingredient assessments are made independently and are not
              influenced by food manufacturers or advertisers. Where a page
              contains advertising, it is clearly part of the page layout and
              does not shape our editorial judgment about any ingredient.
            </p>
          </section>

          <section className="bg-amber-50 rounded-2xl p-8 border border-amber-100" data-id="element-2536" data-mp="2vunvoriynjm2">
            <div className="flex items-center gap-3 mb-4" data-id="element-2537" data-mp="3jiovnc22prpv">
              <div className="bg-amber-100 text-amber-700 p-2 rounded-xl" data-id="element-2538" data-mp="3jhduzl3xd02z">
                <Stethoscope className="w-6 h-6" data-id="element-2539" data-mp="3ohrugergn1pe" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-2540" data-mp="3ajbq2uh2lnaf">
                Medical Disclaimer
              </h2>
            </div>
            <p className="mb-4" data-id="element-2541" data-mp="3ffsc9j4plrkv">
              All content produced by IntelliEats — including articles, guides,
              and the in-app ingredient analysis — is provided for{' '}
              <span className="font-semibold text-slate-900" data-id="element-2542" data-mp="2441adixlfu6l">
                educational and informational purposes only
              </span>
              . It is not a substitute for professional medical, nutritional, or
              dietary advice, diagnosis, or treatment.
            </p>
            <p className="mb-4" data-id="element-2543" data-mp="25iarh9li320t">
              Ingredients approved by regulatory authorities are generally
              considered safe when consumed within established limits.
              IntelliEats helps you make informed decisions, but it does not
              diagnose conditions or provide personalized medical guidance.
            </p>
            <p className="m-0" data-id="element-2544" data-mp="1mkrddrk6qt7j">
              Always seek the advice of a qualified healthcare professional or
              registered dietitian with any questions you may have regarding a
              medical condition, allergy, or your individual dietary needs.
              Never disregard professional advice because of something you read
              here.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-2545" data-mp="2p7mvk327lxgn">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2546" data-mp="2p5sg349pixjy">
              Questions or Corrections
            </h2>
            <p className="mb-4" data-id="element-2547" data-mp="2ywwk58xugy00">
              If you have questions about our editorial process or would like to
              suggest a correction, reach out anytime:
            </p>
            <p className="font-medium text-slate-900" data-id="element-2548" data-mp="268r0yqmfvh2a">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-2549" data-mp="26atw1p2g80dv">
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-2550" data-mp="2k91trz7b75q3">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-2551" data-mp="2k65ti47zo4yh">
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}